import { DocumentAnnotationElement } from '@st/document-components/annotations'
import type { RenderableDocumentAnnotation } from '@st/document-components/annotations'
import { Page, Positioned, Wrap } from '@st/document-rendering'
import { Bookmark, Box, Expand, Space, Spacer } from './document-elements'
import {
  CommentSection,
  FORM_BORDER_GREY,
  PDFFormDocument,
  PDFFormPageContent,
  PageHeading,
  SectionRow
} from './form'
import { ComponentConfig, ComponentConfigType, FormRowConfig } from '@st/ui-config'
import { Fragment } from 'react'
import { match, P } from '@st/pattern'
import { FormationComponent } from './formation-component'
import type { ResolvedFormationPage, VisibleSection } from './formation-layout'
import type { FormationFieldChange, FormationPageFields, FormationPageView } from './render-data'

/**
 * Height of a multiline text field for PDF documents
 */
const FORMATION_MULTILINE_TEXT_HEIGHT = 370

type Props = {
  title: string
  pages: FormationPageView[]
  annotations: RenderableDocumentAnnotation[]
  highlightedPageIds?: string[]
}
export function FormationDocument({ title, pages, annotations, highlightedPageIds = [] }: Props) {
  return (
    <PDFFormDocument title={title}>
      {pages.map((page, index) => (
        <FormationDocumentPage
          key={page.config.id}
          view={page}
          annotations={annotations.filter(({ annotation }) => annotation.pageNumber == index + 1)}
          highlight={highlightedPageIds.includes(page.config.id)}
        />
      ))}
    </PDFFormDocument>
  )
}

export function FormationDocumentPage({
  view,
  annotations,
  highlight
}: {
  view: FormationPageView
  annotations: RenderableDocumentAnnotation[]
  highlight?: boolean
}) {
  return (
    <Page width={650} height={1000}>
      <PDFFormPageContent width={650} height={1000}>
        <FormationDocumentPageContent
          view={view}
          highlight={highlight}
          onFieldChange={NO_FIELD_CHANGE}
        />
      </PDFFormPageContent>
      {annotations.map(({ annotation, author }) => (
        <Positioned key={annotation.id} bounds={annotation.bounds}>
          <DocumentAnnotationElement annotation={annotation} author={author} />
        </Positioned>
      ))}
    </Page>
  )
}

export function FormationDocumentPageContent({
  view,
  highlight,
  onFieldChange
}: {
  view: FormationPageView
  highlight?: boolean
  onFieldChange: FormationFieldChange
}) {
  const { config, fields } = view

  return (
    <>
      {config.title ? (
        <PageHeading hasData={!config.title!.includes('Organizer') && highlight}>
          {config.title!}
        </PageHeading>
      ) : null}
      {config.title ? (
        <Bookmark path={config.id} title={resolveTitle(config.title, highlight)} />
      ) : null}

      {config.title ? <Space h={20} /> : null}

      {config.sections.map((section, index) => {
        const key = `${section.title ?? 'section'}-${index}`
        return (
          <Fragment key={key}>
            <FormationPageSection {...section} fields={fields} onFieldChange={onFieldChange} />
            <Space h={20} />
          </Fragment>
        )
      })}
    </>
  )

  function resolveTitle(title: string, hasData: boolean | undefined) {
    return title.replace(/^.*Organizer.*$/, 'Basic Info') + (hasData ? '*' : '')
  }
}

function FormationPageSection({
  title,
  categoryRows,
  rows,
  fields,
  onFieldChange
}: VisibleSection & {
  fields: FormationPageFields
  onFieldChange: FormationFieldChange
}) {
  const allRows = [...categoryRows, ...rows]
  return (
    <CommentSection title={title}>
      {allRows.map((row, index) => (
        <OrganizerRow key={index} config={row} fields={fields} onFieldChange={onFieldChange} />
      ))}
    </CommentSection>
  )
}

const MULTI_ROW_COMPONENTS_TYPES: ComponentConfigType[] = [
  'LabeledGridTableConfig',
  'GridTableConfig',
  'StandardTableConfig',
  'AddressInputConfig',
  'PartnerInputConfig',
  'ChecklistConfig'
]

function OrganizerRow({
  config,
  fields,
  onFieldChange
}: {
  config: FormRowConfig
  fields: FormationPageFields
  onFieldChange: FormationFieldChange
}) {
  const components = config.components

  if (components.length == 1) {
    const component = components[0]
    if (MULTI_ROW_COMPONENTS_TYPES.includes(component.__typename!)) {
      return <FormationComponent config={component} fields={fields} onFieldChange={onFieldChange} />
    } else if (component.__typename == 'TextInputConfig' && component.isMultiline) {
      return (
        <Box
          width={Infinity}
          height={FORMATION_MULTILINE_TEXT_HEIGHT}
          borderWidth={1}
          borderColor={FORM_BORDER_GREY}
        >
          <FormationComponent config={component} fields={fields} onFieldChange={onFieldChange} />
        </Box>
      )
    }
  }

  const entries = components.filter((c) => !paintsNothing(c))
  const isLoneEntryField = entries.length == 1 && isEntryField(entries[0])

  function renderComponent(c: ComponentConfig, index: number) {
    return (
      <FormationComponent key={index} config={c} fields={fields} onFieldChange={onFieldChange} />
    )
  }

  function rowContents() {
    // Choices keep their natural widths; Wrap moves whole options to the next run.
    if (entries.length > 1 && entries.every(isCheckbox)) {
      return (
        <>
          <Expand>
            <Wrap spacing={16} runSpacing={4}>
              {entries.map(renderComponent)}
            </Wrap>
          </Expand>
          {components.filter(paintsNothing).map(renderComponent)}
        </>
      )
    }

    // Entry columns share the width. Keep zero-width notes in source order so
    // their annotation anchors stay beside the same component.
    let hasEntry = false
    return (
      <>
        {components.map((c, index) => {
          const element = renderComponent(c, index)
          if (paintsNothing(c)) return element
          const needsGap = hasEntry
          hasEntry = true
          return (
            <Fragment key={index}>
              {needsGap && <Space w={8} />}
              <Expand>{element}</Expand>
            </Fragment>
          )
        })}
        {isLoneEntryField && <Spacer />}
      </>
    )
  }

  return (
    <SectionRow bullet={toBullet(config.bulletNumber)} indent={config.indent ?? undefined}>
      {rowContents()}
    </SectionRow>
  )

  function toBullet(n: number | undefined | null) {
    if (!n) return undefined
    return `${n}.`
  }
}

function paintsNothing({ __typename }: ComponentConfig) {
  return match(__typename)
    .with(P.union('NoteWidgetConfig', 'FilesInputConfig'), () => true)
    .otherwise(() => false)
}

function isCheckbox({ __typename }: ComponentConfig) {
  return match(__typename)
    .with(P.union('CheckboxConfig', 'FormSelectCategoryConfig'), () => true)
    .otherwise(() => false)
}

function isEntryField({ __typename }: ComponentConfig) {
  return match(__typename)
    .with(
      P.union('TextInputConfig',
      'MoneyInputConfig',
      'DateInputConfig',
      'NameInputConfig',
      'DropdownConfig'),
      () => true
    )
    .otherwise(() => false)
}

const NO_FIELD_CHANGE: FormationFieldChange = () => undefined
